import React, {
	ReactText,
	useCallback,
	useContext,
	useEffect,
	useMemo
} from "react";
import { shouldShowAddEntityActions } from "@/Utils";
import {
	ZomentumRoutes,
	ZomentumEntities,
	DefaultErrorMessage,
	VendorOpportunity,
	VendorPartner,
	AssociatedUser,
	VendorClientContactRequest,
	VendorClientContact
} from "@/Contracts";
import {
	ClientsRequest,
	ClientContactRequest,
	Client,
	ClientContact
} from "@/Contracts/Clients";
import {
	Opportunity,
	OpportunityRequest,
	OpportunitySharingStatus
} from "@/Contracts/Opportunity";
import { EmailThreadMessage } from "@/Contracts/Others/EmailThread";
import { getClients, getClientContacts } from "../../services/Clients";
import { getOpportunities } from "../../services/Opportunity";
import ZModal from "@atoms/ZModal";
import { useState } from "react";
import Form, { FormInstance } from "antd/lib/form";
import {
	ChildEntityHydrationParams,
	filterOptionsOnSearch
} from "@/Contracts/Utils";
import FormBuilder from "antd-form-builder";
import { ICustomFormItemProps } from "@organisms/ZForm/interface";
import { getTaskContact } from "../../utils";
import {
	AllRolePermissions,
	PricingPlanNames,
	UserRole
} from "@/Contracts/Users";
import { getEntityListForAssignedAccess } from "../../utils";
import useAccessLevel from "../useAccessLevel";
import useFeatureAccess from "../useFeatureAccess";
import {
	ESalesActivityType,
	CommonFunctionFormItemsProps
} from "@/Contracts/Others/SalesActivity";
import ZMessage from "@molecules/ZMessage";
import ZFeatureAccess from "../../UI/atoms/ZFeatureAccess";
import usePermissions from "@/Hooks/usePermissions";
import { Modules, ModulePermission } from "@/Contracts/Permission";
import SalesActivityActionCreators from "../../store/actions/SalesActivity";
import {
	SalesActivityModuleState,
	useSalesActivityModuleDispatch,
	useSalesActivityModuleSelector
} from "../../store";
import { ParentContext } from "../../store/context/ParentContext";
import {
	capitalizeAfterUnderscore,
	isPartnerApp,
	isVendorApp
} from "@/Utils";
import { getAssociatedUsers } from "../../services/Opportunity";
import { IOptionGroupProps } from "@atoms/ZSelect/interface";
import { getAllVendorClientContacts } from "../../services/Vendor/Clients";

const useCommonFunctions = (
	form: FormInstance<any>,
	salesActivityType: ESalesActivityType,
	handleOwnerChange?: ((value: string) => void) | null,
	clientListForOpportunityAssigned?: Client[] | null,
	formItemsProps?: CommonFunctionFormItemsProps
): Record<string, any> => {
	const parentContext = useContext(ParentContext);
	const [opportunitySearchResult, setOpportunitySearchResult] =
		useState<Array<Opportunity> | null>(null);
	const [selectedOppotunity, setSelectedOpportunity] = useState<
		string | null
	>(null);
	const [selectedClient, setSelectedClient] = useState<
		string | null | ReactText
	>(null);
	const [clientSearchResult, setClientSearchResult] =
		useState<Array<Client> | null>(null);
	const [clientContactsSearchResult, setClientContactsSearchResult] =
		useState<Array<ClientContact> | Array<VendorClientContact> | null>(
			null
		);
	const [taskContact, setTaskContact] = useState<
		ClientContact | VendorClientContact | null
	>(null);
	const [emailThreadMessageDrawerOpen, setEmailThreadMessageDrawerOpen] =
		useState<boolean>(false);

	const [selectedOppotunityCache, setSelectedOpportunityCache] =
		useState<Opportunity | null>(null);

	const [partnerSearchResult, setPartnerSearchResult] =
		useState<Array<VendorPartner> | null>(null);
	const [vendorOpportunitySearchResult, setVendorOpportunitySearchResult] =
		useState<Array<VendorOpportunity> | null>(null);
	const [vendorClientSearchResult, setVendorClientSearchResult] =
		useState<Array<VendorOpportunity> | null>(null);
	const [selectedPartner, setSelectedPartner] = useState<string | null>(null);
	const [selectedVendorClient, setSelectedVendorClient] = useState<
		string | null
	>(null);
	const [selectedVendorOpportunity, setSelectedVendorOpportunity] = useState<
		string | null
	>(null);

	const { isLoading, currentActiveSalesActivity } =
		useSalesActivityModuleSelector((state: SalesActivityModuleState) => {
			return {
				isLoading: state.salesActivity.isLoading,
				currentActiveSalesActivity:
					state.salesActivity.currentActiveSalesActivity
			};
		});
	const { isLoading: isUserLoading, userData } = parentContext.user;

	const dispatch = useSalesActivityModuleDispatch();
	const isEntityAssined = useAccessLevel(
		currentActiveSalesActivity?.feed_details?.entity_obj?.owner_id
	);

	const clientCompanyPermission = usePermissions(
		Modules.CLIENT_COMPANY,
		ModulePermission.CREATE_EDIT_DELETE_OWNED
	);

	const clientListForAssignedAccess = getEntityListForAssignedAccess(
		currentActiveSalesActivity,
		ZomentumEntities.Client,
		isEntityAssined
	) as Client[];

	const opportunityListForAssignedAccess = getEntityListForAssignedAccess(
		currentActiveSalesActivity,
		ZomentumEntities.Opportunity,
		isEntityAssined
	) as Opportunity[];

	const getEmailThreadMessage = (): EmailThreadMessage | null => {
		const userCompanyEmailIntegration =
			parentContext.integrations.email.find(
				(email: any) => email.user_id === userData.id
			);
		if (userCompanyEmailIntegration) {
			const emailThreadMessage = new EmailThreadMessage();
			emailThreadMessage.account_id =
				userCompanyEmailIntegration.account_id;
			return emailThreadMessage;
		}
		return null;
	};

	const showEmailThreadMessageDrawer = () => {
		if (
			parentContext.integrations.email.find(
				email => email.user_id === userData.id
			)
		) {
			setEmailThreadMessageDrawerOpen(true);
		} else {
			ZModal.confirm({
				title: "You must authenticate your email account before you can send an email",
				content:
					"If you click on OK, you will be redirected to the settings page where you can authenticate your email account",
				onOk: () => {
					if (salesActivityType === ESalesActivityType.TASK) {
						dispatch(
							SalesActivityActionCreators.editGlobalTaskDrawerClosed()
						);
					}
					parentContext.misc.historyPush(
						ZomentumRoutes.EmailSettings
					);
				}
			});
		}
	};

	const closeEmailThreadMessageDrawer = () => {
		setEmailThreadMessageDrawerOpen(false);
	};

	const showAddOpportunityDrawer = () => {
		parentContext.opportunity.addOpportunityDrawerOpened();
	};

	const closeAddOpportunityDrawer = () => {
		parentContext.opportunity.addOpportunityDrawerClosed();
	};

	const showAddClientDrawer = () => {
		parentContext.client.addClientDrawerOpened();
	};

	const closeAddClientDrawer = () => {
		parentContext.client.addClientDrawerClosed();
	};

	const fetchOpportunities = async (
		opportunityRequest: OpportunityRequest
	) => {
		try {
			const response = await getOpportunities(opportunityRequest);
			return response.data;
		} catch (error) {
			console.error(error);
			return null;
		}
	};

	const fetchClients = async (clientRequest: ClientsRequest) => {
		try {
			const response = await getClients(clientRequest);
			return response.data;
		} catch (error) {
			console.error(error);
			return null;
		}
	};

	const fetchClientContacts = async (clientId: string) => {
		try {
			if (isVendorApp()) {
				const vendorClientContactRequest =
					new VendorClientContactRequest(clientId);
				const response = await getAllVendorClientContacts(
					vendorClientContactRequest
				);
				return response.data;
			} else {
				const clientContactRequest = new ClientContactRequest(clientId);
				const response = await getClientContacts(clientContactRequest);
				return response.data;
			}
		} catch (error) {
			console.error(error);
			return null;
		}
	};

	const updateTaskContact = async (relation: ZomentumEntities) => {
		if (relation === ZomentumEntities.Client) {
			setTaskContact(null);
			const contacts = await fetchClientContacts(
				form.getFieldValue(relation)
			);
			setTaskContact(
				getTaskContact(
					relation,
					form.getFieldValue(relation),
					contacts,
					opportunitySearchResult,
					clientSearchResult
				)
			);
		} else {
			setTaskContact(
				getTaskContact(
					relation,
					form.getFieldValue(relation),
					clientContactsSearchResult,
					opportunitySearchResult,
					clientSearchResult
				)
			);
		}
	};

	const onRelationEntityChange = async (
		relation: ZomentumEntities,
		entityId: ReactText
	) => {
		let clientId: ReactText = "";
		if (relation === ZomentumEntities.Opportunity) {
			const selectedOpportunity = opportunitySearchResult?.find(
				(opportunity: Opportunity) => opportunity.id === entityId
			);
			if (selectedOpportunity) {
				clientId = selectedOpportunity.client_company_id;
			}
		} else if (relation === ZomentumEntities.Client) {
			clientId = entityId;
		}
		if (clientId) {
			const contacts = await fetchClientContacts(clientId.toString());
			setClientContactsSearchResult(contacts);
			setSelectedClient(clientId);
		} else {
			ZMessage.error(DefaultErrorMessage);
		}
	};

	const handleRelationEntityChange = (
		relation: ZomentumEntities,
		entityId: ReactText
	) => {
		switch (salesActivityType) {
			case ESalesActivityType.MEETING:
				onRelationEntityChange(relation, entityId);
				return;
			case ESalesActivityType.TASK:
				updateTaskContact(relation);
				return;
			default:
				return;
		}
	};

	const ZUserMultiSelectFormItem = ({ disabled }: { disabled?: boolean }) => (
		<FormBuilder
			form={form}
			meta={{
				formItemLayout: {
					labelCol: { span: 5 },
					wrapperCol: { span: 19 }
				},
				fields: [
					{
						key: "collaborator_ids",
						label: "Collaborators",
						widget: ({ value, onChange }: ICustomFormItemProps) => (
							<parentContext.user.ZUserSelect
								showSearch={true}
								allowClear={true}
								disabled={
									isLoading || isUserLoading || disabled
								}
								placeholder="Please select collaborators"
								mode="multiple"
								filterOption={filterOptionsOnSearch}
								filterCurrentUser={false}
								value={value}
								onChange={v => onChange(v)}
							/>
						)
					}
				]
			}}
		/>
	);

	const opportunityPermissions = usePermissions(
		Modules.OPPORTUNITY,
		ModulePermission.CREATE_EDIT_DELETE_OWNED
	);

	const ZOpportunitySelectFormItem = ({
		disabled
	}: {
		disabled?: boolean;
	}) => (
		<Form.Item
			noStyle
			shouldUpdate={(prevValues, currentValues) =>
				prevValues.relation !== currentValues.relation
			}
		>
			{({ getFieldValue }) =>
				getFieldValue("relation") === ZomentumEntities.Opportunity && (
					<FormBuilder
						form={form}
						meta={{
							fields: [
								{
									key: ZomentumEntities.Opportunity,
									wrapperCol: { span: 24 },
									widget: ({
										value,
										onChange
									}: ICustomFormItemProps) => {
										const opportunities =
											opportunitySearchResult
												? [
														...opportunitySearchResult,
														...opportunityListForAssignedAccess
												  ]
												: opportunityListForAssignedAccess;

										const hasSelectedOpportunity =
											selectedOppotunityCache &&
											opportunities.find(
												opp =>
													opp.id ===
													selectedOppotunityCache.id
											);

										if (
											!hasSelectedOpportunity &&
											selectedOppotunityCache
										) {
											opportunities.push(
												selectedOppotunityCache
											);
										}

										return (
											<parentContext.opportunity.ZOpportunitySelect
												placeholder="Please select opportunity"
												dropdownRender={menu => (
													<div>
														{menu}
														<ZFeatureAccess
															requiredPlan={
																PricingPlanNames.IGNITE
															}
															requiredRole={
																UserRole.UserRole
															}
															permissions={
																opportunityPermissions
															}
														>
															<div
																className="add-entity-action"
																onMouseDown={$event => {
																	$event.preventDefault();
																}}
																onClick={
																	showAddOpportunityDrawer
																}
															>
																Add Opportunity
															</div>
														</ZFeatureAccess>
													</div>
												)}
												defaultOptions={opportunities}
												value={
													selectedOppotunity ?? value
												}
												onChange={(id, opportunity) => {
													onChange(id);
													setSelectedOpportunity(
														null
													);
													handleRelationEntityChange(
														ZomentumEntities.Opportunity,
														id
													);
													setSelectedOpportunityCache(
														opportunity ?? null
													);
												}}
												disabled={
													formItemsProps
														?.opportunitySelect
														?.disabled ||
													!opportunitySearchResult ||
													isLoading ||
													isUserLoading ||
													disabled
												}
												data-testid="opportunity-select"
											/>
										);
									},
									rules: [
										() => ({
											validator(_: any, value: any) {
												if (
													form.getFieldValue(
														"relation"
													) ===
														ZomentumEntities.Opportunity &&
													!value
												) {
													return Promise.reject(
														new Error(
															"Please select opportunity"
														)
													);
												} else {
													return Promise.resolve();
												}
											}
										})
									]
								}
							]
						}}
					/>
				)
			}
		</Form.Item>
	);

	const ZClientSelectFormItem = ({ disabled }: { disabled?: boolean }) => (
		<Form.Item
			noStyle
			shouldUpdate={(prevValues, currentValues) =>
				prevValues.relation !== currentValues.relation
			}
		>
			{({ getFieldValue }) =>
				getFieldValue("relation") === ZomentumEntities.Client && (
					<FormBuilder
						form={form}
						meta={{
							fields: [
								{
									key: ZomentumEntities.Client,
									wrapperCol: { span: 24 },
									widget: ({
										value,
										onChange
									}: ICustomFormItemProps) => (
										<parentContext.client.ZClientSelect
											childHydrationParams={[
												ChildEntityHydrationParams.CLIENT_USERS_COUNT
											]}
											dropdownRender={menu => (
												<div>
													{menu}
													<ZFeatureAccess
														requiredPlan={
															PricingPlanNames.IGNITE
														}
														requiredRole={
															UserRole.UserRole
														}
														permissions={
															clientCompanyPermission
														}
													>
														{shouldShowAddEntityActions() && (
															<div
																className="add-entity-action"
																onMouseDown={$event => {
																	$event.preventDefault();
																}}
																onClick={
																	showAddClientDrawer
																}
															>
																Add Client
															</div>
														)}
													</ZFeatureAccess>
												</div>
											)}
											defaultOptions={
												clientSearchResult
													? [
															...clientSearchResult,
															...clientListForAssignedAccess,
															...(clientListForOpportunityAssigned ??
																[])
													  ]
													: clientListForAssignedAccess
											}
											placeholder="Please select client"
											value={selectedClient ?? value}
											onChange={v => {
												onChange(v);
												setSelectedClient(null);
												handleRelationEntityChange(
													ZomentumEntities.Client,
													v
												);
											}}
											disabled={
												formItemsProps?.clientSelect
													?.disabled ||
												!clientSearchResult ||
												isLoading ||
												isUserLoading ||
												disabled
											}
											data-testid="client-select"
										/>
									),
									rules: [
										() => ({
											validator(_: any, value: any) {
												if (
													form.getFieldValue(
														"relation"
													) ===
														ZomentumEntities.Client &&
													!value
												) {
													return Promise.reject(
														new Error(
															"Please select client"
														)
													);
												} else {
													return Promise.resolve();
												}
											}
										})
									]
								}
							]
						}}
					/>
				)
			}
		</Form.Item>
	);

	const ZVendorClientSelectFormItem = ({
		disabled
	}: {
		disabled?: boolean;
	}) => (
		<Form.Item
			noStyle
			shouldUpdate={(prevValues, currentValues) =>
				prevValues.relation !== currentValues.relation
			}
		>
			{({ getFieldValue }) =>
				getFieldValue("relation") === ZomentumEntities.VendorClient && (
					<FormBuilder
						form={form}
						meta={{
							fields: [
								{
									key: ZomentumEntities.VendorClient,
									wrapperCol: { span: 24 },
									widget: ({
										value,
										onChange
									}: ICustomFormItemProps) => (
										<parentContext.vendorClient.ZVendorClientSelect
											onChange={v => {
												onChange(v);
											}}
											value={
												selectedVendorClient ?? value
											}
											placeholder="Please select client"
											defaultOptions={[
												...(vendorClientSearchResult ??
													[])
											]}
											disabled={
												!vendorClientSearchResult ||
												isLoading ||
												disabled
											}
										/>
									),
									rules: [
										() => ({
											validator(_: any, value: any) {
												if (
													form.getFieldValue(
														"relation"
													) ===
														ZomentumEntities.VendorClient &&
													!value
												) {
													return Promise.reject(
														new Error(
															"Please select client"
														)
													);
												} else {
													return Promise.resolve();
												}
											}
										})
									]
								}
							]
						}}
					/>
				)
			}
		</Form.Item>
	);

	const ZVendorPartnerSelectFormItem = ({
		disabled
	}: {
		disabled?: boolean;
	}) => (
		<Form.Item
			noStyle
			shouldUpdate={(prevValues, currentValues) =>
				prevValues.relation !== currentValues.relation
			}
		>
			{({ getFieldValue }) =>
				getFieldValue("relation") ===
					ZomentumEntities.VendorCompany && (
					<FormBuilder
						form={form}
						meta={{
							fields: [
								{
									key: ZomentumEntities.VendorCompany,
									wrapperCol: { span: 24 },
									widget: ({
										value,
										onChange
									}: ICustomFormItemProps) => (
										<parentContext.vendorPartner.ZVendorPartnerSelect
											onChange={v => {
												onChange(v);
											}}
											value={selectedPartner ?? value}
											placeholder="Please select partner"
											defaultOptions={[
												...(partnerSearchResult ?? [])
											]}
											disabled={
												!partnerSearchResult ||
												isLoading ||
												disabled
											}
										/>
									),
									rules: [
										() => ({
											validator(_: any, value: any) {
												if (
													form.getFieldValue(
														"relation"
													) ===
														ZomentumEntities.VendorPartner &&
													!value
												) {
													return Promise.reject(
														new Error(
															"Please select partner"
														)
													);
												} else {
													return Promise.resolve();
												}
											}
										})
									]
								}
							]
						}}
					/>
				)
			}
		</Form.Item>
	);

	const ZVendorOpportunitySelectFormItem = ({
		disabled
	}: {
		disabled?: boolean;
	}) => (
		<Form.Item
			noStyle
			shouldUpdate={(prevValues, currentValues) =>
				prevValues.relation !== currentValues.relation
			}
		>
			{({ getFieldValue }) =>
				getFieldValue("relation") ===
					ZomentumEntities.VendorOpportunity && (
					<FormBuilder
						form={form}
						meta={{
							fields: [
								{
									key: ZomentumEntities.VendorOpportunity,
									wrapperCol: { span: 24 },
									widget: ({
										value,
										onChange
									}: ICustomFormItemProps) => (
										<parentContext.opportunity.ZOpportunitySelect
											onChange={v => {
												onChange(v);
											}}
											value={
												selectedVendorOpportunity ??
												value
											}
											placeholder="Please select opportunity"
											defaultOptions={[
												...(vendorOpportunitySearchResult ??
													[])
											]}
											disabled={
												!vendorOpportunitySearchResult ||
												isLoading ||
												disabled
											}
										/>
									),
									rules: [
										() => ({
											validator(_: any, value: any) {
												if (
													form.getFieldValue(
														"relation"
													) ===
														ZomentumEntities.VendorOpportunity &&
													!value
												) {
													return Promise.reject(
														new Error(
															"Please select opportunity"
														)
													);
												} else {
													return Promise.resolve();
												}
											}
										})
									]
								}
							]
						}}
					/>
				)
			}
		</Form.Item>
	);

	const salesActivityPermission = usePermissions(
		Modules.SALES_ACTIVITY,
		ModulePermission.EDIT_ALL,
		[AllRolePermissions.SALES_ACTIVITY_ASSIGN]
	);
	const isFeatureAccess = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.UserRole,
		salesActivityPermission
	);

	const ZUserSelectFormItem = React.memo(
		({
			showAssociatedUsers,
			disabled
		}: {
			showAssociatedUsers?: boolean;
			disabled?: boolean;
		}) => {
			const [loadingAssociatedUsers, setLoadingAssociatedUsers] =
				useState(false);
			const [associatedUsers, setAssociatedUsers] = useState<
				AssociatedUser[]
			>([]);
			const fetchAssociatedUsers = useCallback(async () => {
				try {
					if (
						showAssociatedUsers &&
						parentContext.opportunity.currentActiveOpportunity &&
						((
							parentContext.opportunity
								.currentActiveOpportunity as Opportunity
						).opportunity_vendor_details?.status ===
							OpportunitySharingStatus.ACCEPTED ||
							(
								parentContext.opportunity
									.currentActiveOpportunity as VendorOpportunity
							).partner_opportunity_status ===
								OpportunitySharingStatus.ACCEPTED)
					) {
						setLoadingAssociatedUsers(true);
						const associatedUsers = await getAssociatedUsers(
							parentContext.opportunity.currentActiveOpportunity
								?.id
						);
						setAssociatedUsers(associatedUsers.data);
					}
				} catch (error) {
					console.error(error);
				} finally {
					setLoadingAssociatedUsers(false);
				}
			}, []);
			useEffect(() => {
				fetchAssociatedUsers();
			}, []);
			const associatedUsersOptGroup: IOptionGroupProps[] = useMemo(
				() => [
					{
						label: isPartnerApp() ? "Vendor" : "Partner",
						selectOptions: associatedUsers.map(associatedUser => ({
							key: associatedUser.id,
							value: associatedUser.id,
							name: `${
								associatedUser.name.full
							} (${capitalizeAfterUnderscore(
								associatedUser.associated_user_type
							)})`
						}))
					}
				],
				[associatedUsers]
			);
			return (
				<FormBuilder
					form={form}
					meta={{
						formItemLayout: {
							labelCol: { span: 5 },
							wrapperCol: { span: 19 }
						},
						fields: [
							{
								key: "author_id",
								label: "Owner",
								required: true,
								message: "Please select owner",
								hasFeedback: true,
								initialValue:
									userData &&
									userData.id &&
									userData.id.length > 0
										? userData.id
										: null,
								widget: ({
									value,
									onChange
								}: ICustomFormItemProps) => (
									<parentContext.user.ZUserSelect
										showSearch
										allowClear={false}
										disabled={
											disabled ||
											((formItemsProps?.userSelect
												?.disabled ||
												!isFeatureAccess ||
												!isUserSelectAllowed ||
												isLoading ||
												isUserLoading) &&
												isPartnerApp())
										}
										placeholder="Please select owner"
										filterOption={filterOptionsOnSearch}
										value={value}
										data-testid="owner-select"
										onChange={v => {
											onChange(v);
											handleOwnerChange &&
												handleOwnerChange(v as string);
										}}
										loading={loadingAssociatedUsers}
										optGroup={
											showAssociatedUsers &&
											!!associatedUsers.length
												? associatedUsersOptGroup
												: undefined
										}
									/>
								)
							}
						]
					}}
				/>
			);
		}
	);

	const isUserSelectAllowed = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.SecondaryRole
	);

	return {
		setSelectedOpportunity,
		setOpportunitySearchResult,
		setPartnerSearchResult,
		setVendorClientSearchResult,
		setVendorOpportunitySearchResult,
		setSelectedPartner,
		setSelectedVendorOpportunity,
		setClientSearchResult,
		setClientContactsSearchResult,
		setSelectedClient,
		setSelectedVendorClient,
		setTaskContact,
		selectedClient,
		taskContact,
		opportunitySearchResult,
		clientContactsSearchResult,
		updateTaskContact,
		emailThreadMessageDrawerOpen,
		getEmailThreadMessage,
		showEmailThreadMessageDrawer,
		closeEmailThreadMessageDrawer,
		closeAddOpportunityDrawer,
		closeAddClientDrawer,
		fetchOpportunities,
		fetchClients,
		fetchClientContacts,
		ZUserMultiSelectFormItem,
		ZOpportunitySelectFormItem,
		ZClientSelectFormItem,
		ZVendorPartnerSelectFormItem,
		ZVendorClientSelectFormItem,
		ZVendorOpportunitySelectFormItem,
		ZUserSelectFormItem
	};
};

export default useCommonFunctions;
